import React, { useEffect } from 'react';
import { PersonDataContext } from '../../../../../../contexts/person-data-context';
import { get } from '../../../../../../utils';
import { sortDescending } from '../../../../../documents';
import { IGeneratedCase } from '../../..';
import { Document } from '../../../../../../data-models';
import { LetterItem } from '.././letters/letter-item';
import { Link } from 'react-router-dom';
import { MultipleCardContainer } from '../../../components/tw-components';
import { useUploadDocuments } from '../../../../../../hooks/use-upload-documents';
import { UploadStatus } from '../../../../../../utils/upload-activities';

export interface IOverviewDocument extends Document {
  pending?: boolean;
  uploadStatus?: UploadStatus;
}

type Props = {
  claim: IGeneratedCase;
  headerLink?: string;
  setDocumentCount: React.Dispatch<React.SetStateAction<number | null>>;
};
export const OverviewDocuments = ({
  claim,
  headerLink,
  setDocumentCount,
}: Props) => {
  const { personData } = React.useContext(PersonDataContext);
  const uploadDocuments = useUploadDocuments();

  const isForClaim = (letter: Document) =>
    claim.claims.find(
      (c) => c.caseId === letter.claimId || c.caseId === letter.leaveId,
    ) ||
    claim.leaves.find(
      (l) => l.caseId === letter.claimId || l.caseId === letter.leaveId,
    ) ||
    claim.accommodations.find(
      (a) => a.caseId === letter.claimId || a.caseId === letter.leaveId,
    );

  const combinedDocuments: IOverviewDocument[] = [
    ...get(personData, 'documents.documents', []),
    ...uploadDocuments,
  ]
    .filter(isForClaim)
    .sort(sortDescending);

  useEffect(() => {
    const count =
      combinedDocuments?.length === 0 ? null : combinedDocuments?.length;
    setDocumentCount(count);
  }, [combinedDocuments?.length, setDocumentCount]);

  const visibleDocuments = combinedDocuments.slice(0, 5);
  const remainingDocumentsCount = Math.max(combinedDocuments.length - 5, 0);

  return (
    <MultipleCardContainer id="documents-card">
      {combinedDocuments.length > 0 ? (
        <div className="tw-flex tw-flex-col tw-w-full tw-overflow-hidden tw-rounded-md tw-bg-white">
          <div className="tw-divide-y tw-divide-unm-interactive01-400">
            {visibleDocuments.map((document, index) => (
              <LetterItem
                item={document}
                index={index}
                key={`${document.claimId}-${document.receivedDate}-${index}`}
              />
            ))}
          </div>
          {remainingDocumentsCount > 0 && headerLink && (
            <div
              className="tw-p-4 tw-flex tw-justify-end tw-border-t 
                            tw-border-solid tw-border-0 tw-border-unm-border1"
            >
              <Link
                to={headerLink}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold 
                          tw-text-unm-interactive01-400 tw-no-underline"
              >
                <span>View more documents ({remainingDocumentsCount})</span>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  className="tw-h-4 tw-w-4 tw-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 3.5 10.5 8 5 12.5" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div
          className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-unm-primary02-400 tw-text-md tw-p-4 
                        tw-pointer-events-none tw-bg-unm-bg-light"
        >
          You currently have no documents uploaded.
        </div>
      )}
      <div className="tw-font-normal tw-text-sm tw-px-4 tw-py-3 tw-text-unm-adhoc2">
        Please note, some documents may not be visible in the portal due to
        compliance restrictions. Any viewable document will be available within
        five business days from receipt.
      </div>
    </MultipleCardContainer>
  );
};
